import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodes, COMMAND_PRIORITY_LOW } from 'lexical';
import { PASTE_COMMAND } from 'lexical';
import { $createImageNode } from '../nodes/ImageNode';
import { v4 as uuidv4 } from 'uuid';

import { putImageBlob } from '@/src/utils';

async function getImageSize(blob: Blob): Promise<{ width: number; height: number }> {
    // Fast path
    if ('createImageBitmap' in window) {
        const bmp = await createImageBitmap(blob);
        const size = { width: bmp.width, height: bmp.height };
        bmp.close?.();
        return size;
    }
    // Fallback
    const url = URL.createObjectURL(blob);
    try {
        const img = new Image();
        img.src = url;
        await img.decode();
        return { width: img.naturalWidth, height: img.naturalHeight };
    } finally {
        URL.revokeObjectURL(url);
    }
}

function fitWidthKeepAspect(w: number, h: number, maxW = 600) {
    if (w <= maxW) return { width: w, height: h };
    const scale = maxW / w;
    return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

export default function PasteImagePlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand<ClipboardEvent>(
            PASTE_COMMAND,
            (event) => {
                const items = event.clipboardData?.items;
                if (!items) return false;

                let handled = false;

                for (const item of items) {
                    if (item.type?.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (!file) continue;

                        handled = true;
                        event.preventDefault();

                        (async () => {
                            const guid = uuidv4();

                            const fileType = file.type.replace('image/', '') || 'unknown';
                            const fileName = `${guid}.${fileType}`;

                            // stash blob so local://GUID can render
                            putImageBlob(fileName, file);

                            const { width: w, height: h } = await getImageSize(file);
                            const { width, height } = fitWidthKeepAspect(w, h, 600);

                            editor.update(() => {
                                $insertNodes([
                                    $createImageNode({
                                        src: `local://${fileName}`,
                                        width,
                                        height,
                                    }),
                                ]);
                            });
                        })();
                    }
                }

                return handled;
            },
            COMMAND_PRIORITY_LOW
        );
    }, [editor]);

    return null;
}
